import { FieldId } from "@redotech/redo-return-core-common/return-flow-answers";
import { offeringSelectionSchema } from "@redotech/redo-return-warranties-common/extended-warranty";
import { PurchaseChannel } from "@redotech/redo-return-warranties-common/warranties";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DraftReturnSchema } from "../draft-return/draft-return";
import { WarrantyProductSchema } from "../warranty-registration";

export const WARRANTY_REGISTRATION_PREFIX = "WTY";

const productVariantResponseSchema = z.object({
  type: createEnum([
    "images",
    "text_area",
    "multiple_choice",
    "videos",
    "date",
  ]),
  value: z.union([z.string(), z.array(z.string())]),
  message: z.string(),
  fieldId: createEnum(FieldId).nullish(),
});

export type ProductVariantResponse = z.infer<
  typeof productVariantResponseSchema
>;

/**
 * The purchase date of the registered item, from the response marked with the
 * `WARRANTIES_ORDER_DATE` field id. Invalid and future dates are ignored.
 */
export function getPurchaseDateFromResponses(
  responses:
    | readonly Pick<ProductVariantResponse, "fieldId" | "value">[]
    | null
    | undefined,
): Date | undefined {
  const response = responses?.find(
    (response) =>
      response.fieldId === FieldId.WARRANTIES_ORDER_DATE &&
      typeof response.value === "string",
  );
  if (!response) {
    return undefined;
  }
  const date = new Date(response.value as string);
  if (isNaN(date.getTime()) || date.getTime() > Date.now()) {
    return undefined;
  }
  return date;
}

const sourceSchema = z.object({
  purchaseChannel: createEnum(PurchaseChannel),
  orderId: z.string().nullish(),
  referenceId: z.string().nullish(),
  channelName: z.string().nullish(),
});

// TODO: Reconcile with SourceDetails
export type RegistrationSource = z.infer<typeof sourceSchema>;

const warrantyRegistrationAccountSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string().optional(),
  address: z.object({
    street1: z.string(),
    street2: z.string(),
    state: z.string(),
    city: z.string(),
    country: z.string(),
    zip: z.string(),
  }),
});

export type WarrantyRegistrationAccount = z.infer<
  typeof warrantyRegistrationAccountSchema
>;

export const submitWarrantyRegistrationSchema = {
  name: "submitWarrantyRegistration" as const,
  input: z.object({
    draftReturnId: z.string().optional(),
    notifyCustomer: z.boolean().default(false),
    product: WarrantyProductSchema,
    responses: z.array(productVariantResponseSchema).readonly().nullish(),
    account: warrantyRegistrationAccountSchema,
    offeringSelection: offeringSelectionSchema.nullish(),
    paymentToken: z.string().nullish(),
    optInMarketing: z.boolean(),
    source: sourceSchema.nullish(),
    requiresProductValidation: z.boolean().optional(),
    customerProvidedProductName: z.string().optional(),
    customerProvidedProductPhotos: z.array(z.string()).optional(),
    customerProvidedProductPrice: z.string().optional(),
  }),
  output: z.object({
    success: z.boolean(),
    draftReturn: DraftReturnSchema.nullish(),
    warrantyRegistrationId: z.string().nullish(),
    // Disabling login because of security concerns. Will be re-enabled when we have a OTP in registration.
    // customerToken: z.string().nullish(),
    // customer: z
    //   .object({
    //     name: z.string(),
    //     email: z.string(),
    //     customerId: z.string().nullish(),
    //     firstName: z.string().nullish(),
    //     lastName: z.string().nullish(),
    //   })
    //   .nullish(),
  }),
};

export type SubmitWarrantyRegistrationInput = z.infer<
  (typeof submitWarrantyRegistrationSchema)["input"]
>;

export type SubmitWarrantyRegistrationOutput = z.infer<
  (typeof submitWarrantyRegistrationSchema)["output"]
>;
